// App root: the mobile shell plus the hash router. Five tabs, and every
// other screen is a destination one tap from a tab, so no feature is more
// than two taps away. No react-router.
import { AppProvider, useApp } from "./state/app";
import { Shell } from "./components/Shell";
import { goBack, PARENT_OF, useRoute, type RouteName } from "./lib/router";
import { AuthScreen } from "./screens/AuthScreen";
import { BudgetScreen } from "./screens/BudgetScreen";
import { CaptureScreen } from "./screens/CaptureScreen";
import { DiaryScreen } from "./screens/DiaryScreen";
import { DiscoverScreen } from "./screens/DiscoverScreen";
import { EditProfileScreen } from "./screens/EditProfileScreen";
import { HomeScreen } from "./screens/HomeScreen";
import { ItineraryScreen } from "./screens/ItineraryScreen";
import { MapScreen } from "./screens/MapScreen";
import { ProfileScreen } from "./screens/ProfileScreen";
import { SearchScreen } from "./screens/SearchScreen";
import { StatsScreen } from "./screens/StatsScreen";
import { TimelineScreen } from "./screens/TimelineScreen";
import { TripsScreen } from "./screens/TripsScreen";
import { WishlistScreen } from "./screens/WishlistScreen";
import type { TranslationKey } from "./lib/i18n";
import "./styles.css";

const TITLE_KEYS: Record<RouteName, TranslationKey> = {
  beranda: "home.title",
  map: "nav.map",
  timeline: "search.title",
  cari: "cari.title",
  profil: "profil.title",
  capture: "capture.title",
  search: "search.title",
  trips: "trips.title",
  diary: "diary.title",
  itinerary: "itinerary.title",
  budget: "budget.title",
  stats: "stats.title",
  wishlist: "wishlist.title",
};

/**
 * Screens that manage their own scrolling: the map canvas, the viewfinder,
 * the swipe deck and the search grid all fill the viewport and never hand a
 * page scroll to the shell. Anything inside them that CAN overflow owns its
 * own scroll container, because `bleed` is a per route decision while
 * overflow belongs to whatever actually overflows.
 */
const BLEED: ReadonlySet<RouteName> = new Set<RouteName>(["map", "capture", "cari", "search"]);

function Screens() {
  const route = useRoute();
  const { t, activeTrip, authState } = useApp();
  const parent = PARENT_OF[route.name];
  // A tab is its own parent AND carries no param. `#/profil/edit` is a
  // destination inside the Profil tab, so it keeps the tab lit and still
  // gets a back control.
  const isTab = parent === route.name && route.param === null;

  let title = t(TITLE_KEYS[route.name]);
  if (route.name === "map" && activeTrip) title = activeTrip.title;
  if (route.name === "timeline") title = t("nav.timeline");
  if (route.name === "diary" && route.param === "calendar") title = t("calendar.title");
  if (route.name === "profil" && route.param === "edit") title = t("profile.edit");

  // Only a definite "nobody is signed in" answer shows the login screen. On
  // boot, and when the API cannot be reached at all, the app renders as
  // normal against its cached data: this is an offline first diary.
  if (authState === "anonymous") {
    return (
      <Shell active="beranda" title={t("app.title")} hideTabs>
        <AuthScreen />
      </Shell>
    );
  }

  return (
    <Shell
      active={parent}
      title={title}
      bleed={BLEED.has(route.name)}
      onBack={isTab ? undefined : () => goBack(parent)}
    >
      {route.name === "beranda" ? <HomeScreen /> : null}
      {route.name === "map" ? <MapScreen /> : null}
      {route.name === "timeline" ? <TimelineScreen /> : null}
      {route.name === "cari" ? <DiscoverScreen /> : null}
      {route.name === "profil" ? (
        route.param === "edit" ? <EditProfileScreen /> : <ProfileScreen />
      ) : null}
      {route.name === "capture" ? <CaptureScreen /> : null}
      {route.name === "search" ? <SearchScreen /> : null}
      {route.name === "trips" ? <TripsScreen route={route} /> : null}
      {route.name === "diary" ? <DiaryScreen route={route} /> : null}
      {route.name === "itinerary" ? <ItineraryScreen /> : null}
      {route.name === "budget" ? <BudgetScreen /> : null}
      {route.name === "stats" ? <StatsScreen /> : null}
      {route.name === "wishlist" ? <WishlistScreen /> : null}
    </Shell>
  );
}

export function App() {
  return (
    <AppProvider>
      <Screens />
    </AppProvider>
  );
}
